import React, { CSSProperties } from "react";

interface PromotionBannerExportProps {
  title?: string;
  subtitle?: string;
  discount?: string;
  buttonText?: string;
  buttonLink?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundZoom?: number;
  backgroundColor?: string;
  accentColor?: string;
  textColor?: string;
  titleColor?: string;
  subtitleColor?: string;
  layout?: "left" | "center" | "right";
  showBadge?: boolean;
  badgeText?: string;
  expiryText?: string;
  textFontFamily?: string;
}

export const PromotionBannerExport: React.FC<PromotionBannerExportProps> = ({
  title = "Special Promotion!",
  subtitle = "Limited time offer for our valued customers",
  discount = "20% OFF",
  buttonText = "Book Now",
  buttonLink = "/contact",
  backgroundImage,
  backgroundPosition = "50% 50%",
  backgroundZoom = 1,
  backgroundColor = "#7c3aed",
  accentColor = "#fbbf24",
  textColor = "#ffffff",
  titleColor,
  subtitleColor,
  layout = "left",
  showBadge = true,
  badgeText = "Limited Time",
  expiryText = "Offer ends soon!",
  textFontFamily,
}) => {
  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const ffStyle = (text?: string): CSSProperties => textFontFamily ? { fontFamily: `'${textFontFamily}', var(--font-family), sans-serif` } : (text && !hasThai(text)) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {};
  const bgPosition = backgroundPosition || "50% 50%";

  const containerStyle: CSSProperties = {
    position: "relative",
    padding: "48px 24px",
    backgroundColor: backgroundImage ? undefined : backgroundColor,
    overflow: "hidden",
  };

  const contentStyle: CSSProperties = {
    position: "relative",
    zIndex: 10,
    maxWidth: "896px",
    margin: "0 auto",
    textAlign: layout as CSSProperties["textAlign"],
    display: "flex",
    flexDirection: "column",
    alignItems: layout === "center" ? "center" : layout === "right" ? "flex-end" : "flex-start",
  };

  return (
    <div style={containerStyle} className="promotion-banner">
      {/* Background image with zoom/position */}
      {backgroundImage && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: bgPosition,
            backgroundRepeat: "no-repeat",
            transform: `scale(${backgroundZoom || 1})`,
            transformOrigin: bgPosition,
          }}
        />
      )}
      {/* Overlay for background image */}
      {backgroundImage && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: backgroundColor,
            opacity: 0.85,
          }}
        />
      )}

      {/* Decorative circles */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "256px",
          height: "256px",
          opacity: 0.1,
        }}
      >
        <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
          <circle cx="100" cy="100" r="80" fill={accentColor} />
        </svg>
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "192px",
          height: "192px",
          opacity: 0.1,
          transform: "translate(-25%, 25%)",
        }}
      >
        <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
          <circle cx="100" cy="100" r="80" fill={accentColor} />
        </svg>
      </div>

      <div style={contentStyle}>
            {/* Badge with star icon */}
            {showBadge && badgeText && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "4px 16px",
                  backgroundColor: accentColor,
                  color: backgroundColor,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderRadius: "24px",
                  marginBottom: "16px",
                }}
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {badgeText}
              </span>
            )}
            {discount && (
              <div
                style={{
                  fontSize: "3rem",
                  fontWeight: 900,
                  color: accentColor,
                  marginBottom: "8px",
                }}
              >
                {discount}
              </div>
            )}
            <h2
              style={{
                fontSize: "2rem",
                fontWeight: 700,
                color: titleColor || textColor,
                marginBottom: "8px",
                marginTop: 0,
                ...ffStyle(title),
              }}
            >
              {title}
            </h2>
            {subtitle && (
              <p
                style={{
                  fontSize: "1.125rem",
                  color: subtitleColor || textColor,
                  opacity: 0.9,
                  marginBottom: "16px",
                  marginTop: 0,
                  ...ffStyle(subtitle || ""),
                }}
              >
                {subtitle}
              </p>
            )}
            {/* Expiry with clock icon */}
            {expiryText && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "0.875rem",
                  color: accentColor,
                  marginBottom: "24px",
                }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {expiryText}
              </div>
            )}
            {/* Button with arrow icon */}
            {buttonText && (
              <a
                href={buttonLink}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  backgroundColor: accentColor,
                  color: backgroundColor,
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "1.125rem",
                  textDecoration: "none",
                  transition: "all 0.2s",
                }}
              >
                {buttonText}
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            )}
      </div>
    </div>
  );
};
